/**
 * This implements all of the needed functionality for a datasource.
 */
import SeekTransport from "common/framework/SeekTransport";
import flattenFilter from "common/framework/flattenFilter";
export default {

    /**
     * Mark an item as needing deleted, even if the item is not currently in the datasource.
     * @param remove Item to remove
     */
    forceRemove: function(remove) {
        var item = this.get(remove.id);
        if (!item) {
            item = this.add(remove);
        }
        this.remove(item);
    },

    /**
     * Return the expanded version of the given item.  Relies on the transport.read object to contain enough data to know where to make the
     * /{id} call.  Assumes that transport.read.url ends with an "/".
     * @param itemID
     * @param callback
     */
    getExpanded: function(itemID, callback) {
        if (this._readConfig) {
            var config = $.extend({}, this._readConfig);
            config.url += itemID;
            var xhr = $.ajax(config);
            if (callback) {
                xhr.done(callback);
            }
            return xhr;
        }
        return $.Deferred().reject().promise();
    },

    _getInitialFilter: function() {
        if (!this._initialFilter) {
            return;
        }
        var res = {
            logic: this._initialFilter.logic,
            filters: []
        };
        for (var i = 0; i < this._initialFilter.filters.length; i++) {
            res.filters.push({
                field: this._initialFilter.filters[i].field,
                operator: this._initialFilter.filters[i].operator,
                value: this._initialFilter.filters[i].value
            });
        }
        return res;
    },

    /**
     * Seek to the page containing the given item.
     * This also resets the filters to the initial filters.
     *
     * If paging is configured, the seek transport must be defined.  Otherwise, this just
     * resets the filters.
     * @param item Item to find.
     * @return a promise that is resolved once the correct page has been loaded
     */
    seek: function(item) {
        var go = $.proxy(function(page) {
            return this.query({
                filter: this._getInitialFilter(),
                sort: this.sort(),
                pageSize: this.pageSize(),
                group: this.group(),
                page: parseInt(page)
            });
        }, this);
        if (!this.transport.setup || !this.options.serverPaging || !this.options.transport || !this.options.transport.seek) {
            return go(0);
        } else {
            var def = $.Deferred();
            $.ajax(this.transport.setup({
                data: {
                    item: item,
                    filter: this._getInitialFilter(),
                    sort: this.sort(),
                    pageSize: this.pageSize()
                },
                success: function(p) {
                    go(p)
                        .fail(function() {
                            def.reject();
                        })
                        .done(function() {
                            def.resolve();
                        });
                },
                error: function() {
                    go(1).always(function() {
                        def.reject();
                    });
                }
            }, "seek")).handle("error/message", function() {});
            return def.promise();
        }
    },

    /**
     * Find the index of an item by using the seek method.
     * @param item Item to find
     * @returns {*} Promise that will be resolved with a 0-based index
     */
    findRemoteIndex: function(item) {
        var def = $.ajax(this.transport.setup({
            data: {
                item: item,
                filter: this.filter(),
                sort: this.sort(),
                pageSize: 1
            }
        }, "seek"));
        def.handle("error/message", function() {});
        return def.then(function(oneBased) {
            return oneBased - 1;
        });
    },

    /**
     * Return a promise that will be resolved when the given event is fired next.  It will be rejected on error.
     * @param event
     */
    promise: function(event) {
        if (!event) {
            return $.Deferred().resolve(this);
        }
        var def = $.Deferred();
        var resolve = function(e) {
            def.resolve(e);
        };
        var reject = function(e) {
            def.reject(e);
        };
        this.one(event, resolve);
        this.one("error", reject);
        var promise = def.promise();
        var dsH = $.proxy(this.handle, this);
        promise.handle = function() {
            dsH.apply(this, arguments);
            return promise;
        };
        return promise;
    },

    /**
     * Handle an error locally, and prevent them from bubbling up to the global error handling.  Calling this will
     * undo any previous calls to handle with same type.
     * @param type One of the error types the error manager throws
     * @param callback Will be called only if an error occurs with the same argument the error manager provides
     */
    handle: function(type, callback) {
        var stop = function() {
            this.unbind("sync", stop);
            this.unbind(type, c);
        };
        var c = function(e) {
            e.preventDefault();
            callback(e);
            stop.call(this);
        };
        this.unbind(type);
        this.bind(type, c);
        this.bind("sync", stop)
        return this;
    },

    /**
     * Determine whether this data source is grouped.
     *
     * @return {boolean} true when grouped, otherwise false
     */
    isGrouped: function() {
        var groups = this.group() || [];
        return (groups.length > 0);
    },

    /**
     * This overrides an internal kendo function.
     * This is not the best idea, but there is no clean way to catch when a delete occurs.
     * The goal is to catch when delete failures occur, then reset the datasource so that the attempted deleted items
     * exist in the datasource.
     * @private
     */
    _send: function(method) {
        var res = this.super.fn._send.apply(this, arguments);
        var me = this;
        if (method == "destroy") {
            $.when.apply($, res).fail(function() {
                me.cancelChanges();
            });
        }
        return res;
    },

    init: function(config) {
        //create a copy of the config so that changes don't effect the original

        config = $.extend({}, config);

        //store transport for use in getExpaned
        if (config.transport && config.transport.read && config.transport.read.url) {
            this._readConfig = $.extend({}, config.transport.read);
        }

        if (config.serverPagingConfig && config.serverPaging) {
            if (!config.schema) {
                config.schema = {};
            }
            config.schema.data = function(res) {
                if (res && res.results) {
                    return res.results;
                } else if (res && Array.isArray(res)) {
                    return res;
                } else {
                    return [];
                }
            };
            config.schema.total = "totalAvailable";
        }

        if (config.serverGroupingConfig && config.serverGrouping) {
            if (!config.schema) {
                config.schema = {};
            }
            config.schema.groups = function(data) {
                return data;
            }
        }

        if (config.transport && config.transport.parameterMap == "default") {
            config.transport.parameterMap = function(data, type) {
                if (config.transport.preParameterMap) {
                    data = config.transport.preParameterMap(data, type);
                }
                if (type == "read" || type == "seek") {
                    var res = {};
                    if (config.serverPaging) {
                        res.startIndex = data.skip;
                        res.pageSize = data.pageSize;
                        delete data.skip;
                        delete data.pageSize;
                        delete data.take;
                        delete data.page;
                    }
                    if (config.serverSorting) {
                        res.sort = {
                            sorts: data.sort
                        };
                        if(config.serializeDefaultParams !== false) {
                            res.sort = kendo.stringify(res.sort);
                        }
                        delete data.sort;
                    }
                    if (config.serverFiltering) {

                        var filter = data.filter;
                        if (config.flattenFilters && data.filter && data.filter.filters) {
                            var flatFilters = [];
                            filter = {
                                logic: filter.logic,
                                filters: flatFilters
                            };
                            var flatten = function(filters) {
                                filters.forEach(function(filter) {
                                    if (filter.filters) {
                                        flatten(filter.filters);
                                    } else {
                                        flatFilters.push({
                                            field: filter.field,
                                            operator: filter.operator,
                                            value: filter.value
                                        });
                                    }
                                });
                            };
                            flatten(data.filter.filters);
                        }

                        if (filter && filter.filters) {
                            for (var i = 0; i < filter.filters.length; i++) {
                                if (filter.filters[i].value instanceof Date) {
                                    filter.filters[i].value = filter.filters[i].value.getTime().toString();
                                }
                            }
                        }
                        if (filter) {
                            res.filter = filter;
                            if(config.serializeDefaultParams !== false) {
                                res.filter = kendo.stringify(res.filter);
                            }
                        }
                        delete data.filter;
                    }
                    if (type == "seek") {
                        // look for the id of the item.  If not found, use name.
                        res.id = data.item.id;
                        if (!res.id) {
                            res.name = data.item.name;
                        }
                        delete data.item;
                    }
                    $.extend(res, data);
                    if(config.transport.postParameterMap) {
                        res = config.transport.postParameterMap(res, type);
                    }
                    return res;
                } else if (config.transport[type] && config.transport[type].sendField) {
                    return kendo.stringify(config.batch ? data.models.map(function(model) {
                        return model[config.transport[type].sendField];
                    }) : data[config.transport[type].sendField]);
                } else {
                    return kendo.stringify(config.batch ? data.models : data);
                }
            };
        }

        if (config.transport && config.transport.parameterMap) {
            var old = config.transport.parameterMap;
            config.transport.parameterMap = function(data, type) {
                if ((type === "read" || type === "seek") && !!data.filter) {
                    data.filter = flattenFilter(data.filter);
                }
                return old.apply(this, arguments);
            }
        }

        // defaults to true, and used in NestedData only; handles our markedForDelete deletion pattern;
        this.options.useMarkedForDelete = true;

        this.super.fn.init.call(this, config);

        this.errorInit(config);

        this.bind("change", function() {
            //Kendo does not ever update the page manually.
            //This ensures that the current page can never be set past the last page
            var page = this.page();
            var total = this.totalPages();
            if (page !== undefined && page > total && total !== 0) {
                this.page(total);
            }
        });

        if (this._filter) {
            this._initialFilter = flattenFilter(this._filter);
        }
    }

};
